import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { 
  Activity, Waves, Heart, Brain, Sparkles, Users, Target,
  TrendingUp, AlertCircle, CheckCircle2, Lightbulb, ArrowLeft, ExternalLink
} from 'lucide-react';
import EnergyFrequencyTracker from './EnergyFrequencyTracker';

interface DimensionDetail {
  dimension: string;
  score: number;
  icon: any;
  color: string;
  description: string;
  insights: string[];
  recommendations: string[];
  relatedActivities: string[];
}

interface DimensionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dimension: DimensionDetail | null;
}

const dimensionInfo: Record<string, {
  description: string;
  insights: (score: number) => string[];
  recommendations: (score: number) => string[];
  relatedActivities: string[];
}> = {
  Energy: {
    description: "Your physical and mental vitality - the fuel that powers your day. Everything is energy, and your vibrational frequency directly shapes your reality. This reflects your sleep quality, nutrition, movement, and the energetic state you're embodying.",
    insights: (score) => [
      score >= 70 ? "Your energy levels are strong and consistent - you're vibrating at a high frequency" : score >= 40 ? "Your energy fluctuates throughout the day - your vibrational state is shifting" : "You're experiencing low energy that needs attention - your frequency needs support to rise",
      "Energy is directly connected to sleep, nutrition, stress levels, and your vibrational frequency",
      "When you shift your energy, you shift your reality - raising your vibration changes what you attract",
      "Morning routines and consistent sleep schedules boost sustained energy and stabilise your frequency"
    ],
    recommendations: (score) => score >= 70 ? [
      "Maintain your current sleep schedule and high-vibe practices",
      "Keep up your exercise routine to sustain your energetic frequency",
      "Share your high-energy state with others - your vibration is contagious"
    ] : [
      "Prioritise 7-9 hours of quality sleep to restore your energetic body",
      "Add a 10-minute morning walk to boost energy and raise your frequency",
      "Check your hydration - aim for 8 glasses of water daily (water holds vibration)",
      "Practice the Vibrational Frequency tracker to consciously shift your energy state",
      "Use breathwork or meditation to elevate your vibrational frequency"
    ],
    relatedActivities: ["Vibrational Frequency tracker", "Sleep tracking", "Exercise logging", "Breathwork", "Morning routine"]
  },
  Body: {
    description: "Your physical health and body awareness. This includes how your body feels, your movement patterns, sleep quality, and physical self-care.",
    insights: (score) => [
      score >= 70 ? "You're in tune with your body's needs" : score >= 40 ? "Your body awareness could be stronger" : "Your body is signaling it needs more care",
      "Physical health directly impacts mental wellbeing",
      "Regular movement and quality sleep are foundational to body wellness"
    ],
    recommendations: (score) => score >= 70 ? [
      "Continue your body-care practices",
      "Explore new movement activities",
      "Practice body gratitude exercises"
    ] : [
      "Schedule a check-up if you have persistent symptoms",
      "Start with gentle movement - 15 minutes of walking",
      "Create a bedtime routine for better sleep",
      "Track patterns between body sensations and stress"
    ],
    relatedActivities: ["Exercise tracker", "Sleep insights", "Body scan meditation", "Nutrition planning"]
  },
  Emotion: {
    description: "Your emotional landscape and regulation. This reflects how you're feeling, your ability to process emotions, and emotional resilience.",
    insights: (score) => [
      score >= 70 ? "You're emotionally regulated and resilient" : score >= 40 ? "Your emotions need more attention and processing" : "You're experiencing emotional distress that needs support",
      "All emotions are valid and provide important information",
      "Emotional wellness improves with awareness and expression"
    ],
    recommendations: (score) => score >= 70 ? [
      "Keep journaling to maintain emotional awareness",
      "Help others by sharing your emotional regulation skills",
      "Explore deeper emotional growth work"
    ] : [
      "Start a daily emotion check-in practice",
      "Try the TIPP skill when emotions feel overwhelming",
      "Connect with supportive friends or a therapist",
      "Use grounding exercises during difficult moments"
    ],
    relatedActivities: ["Emotion journaling", "Sanctuary skills", "Therapy chat", "Grounding exercises"]
  },
  Mind: {
    description: "Your mental clarity, focus, and cognitive wellness. This includes your headspace, mental overwhelm, and ability to concentrate.",
    insights: (score) => [
      score >= 70 ? "Your mind is clear and focused" : score >= 40 ? "You're experiencing some mental fog or distraction" : "Your mental space feels cluttered and overwhelmed",
      "Mental clarity improves with rest, boundaries, and mindfulness",
      "Cognitive health is connected to sleep, stress, and information overload"
    ],
    recommendations: (score) => score >= 70 ? [
      "Maintain your mental hygiene practices",
      "Challenge your mind with new learning",
      "Share your focus techniques with others"
    ] : [
      "Take regular breaks from screens and information",
      "Try a 5-minute meditation to clear mental clutter",
      "Write down racing thoughts to externalize them",
      "Limit multitasking and focus on one task at a time"
    ],
    relatedActivities: ["Meditation practice", "Brain dump journaling", "Breathwork", "Digital detox"]
  },
  Spirit: {
    description: "Your spiritual connection and sense of meaning. This reflects your connection to something greater than yourself, whether that's nature, spirituality, or purpose.",
    insights: (score) => [
      score >= 70 ? "You feel deeply connected to your spiritual self" : score >= 40 ? "Your spiritual connection needs nurturing" : "You're feeling disconnected from deeper meaning",
      "Spiritual wellness provides resilience during difficult times",
      "Connection can be found in nature, creativity, community, or faith"
    ],
    recommendations: (score) => score >= 70 ? [
      "Deepen your spiritual practice",
      "Share your spiritual insights with others",
      "Explore new dimensions of your spirituality"
    ] : [
      "Spend 10 minutes in nature daily",
      "Try a gratitude practice before bed",
      "Explore what 'spirituality' means to you personally",
      "Connect with something larger - art, music, nature, or community"
    ],
    relatedActivities: ["Nature time", "Gratitude journal", "Meditation", "Oracle cards", "Telempathy tools"]
  },
  Social: {
    description: "Your relationships and sense of connection to others. This includes feeling supported, your ability to be present with others, and relationship quality.",
    insights: (score) => [
      score >= 70 ? "You feel connected and supported in your relationships" : score >= 40 ? "Your social connections need more attention" : "You're feeling isolated or disconnected from others",
      "Humans are wired for connection - it's essential for wellbeing",
      "Quality of relationships matters more than quantity"
    ],
    recommendations: (score) => score >= 70 ? [
      "Nurture your important relationships",
      "Be present and engaged in social interactions",
      "Offer support to someone who might need it"
    ] : [
      "Reach out to one friend or family member today",
      "Join a group or community aligned with your interests",
      "Practice active listening in your next conversation",
      "Consider if any relationships need boundaries or repair"
    ],
    relatedActivities: ["Community connection", "Shared journeys", "Support groups", "Social activities"]
  },
  Purpose: {
    description: "Your sense of direction and meaning in life. This reflects your goals, values alignment, and feeling that your life has purpose.",
    insights: (score) => [
      score >= 70 ? "You have clear direction and feel aligned with your purpose" : score >= 40 ? "You're searching for clearer direction" : "You're feeling lost or unclear about your path",
      "Purpose evolves throughout life - it's not fixed",
      "Meaning can be found in work, relationships, creativity, or service"
    ],
    recommendations: (score) => score >= 70 ? [
      "Set new meaningful goals aligned with your values",
      "Mentor others or share your gifts",
      "Reflect on how your purpose is evolving"
    ] : [
      "Complete the Future Self Letter exercise",
      "Identify three values that matter most to you",
      "Set one small meaningful goal for this week",
      "Explore what brings you joy and energy - purpose lives there"
    ],
    relatedActivities: ["Future Self Navigator", "Life Expansion Calendar", "Vision board", "Manifestation work"]
  }
};

export default function DimensionDetailDialog({ 
  open, 
  onOpenChange, 
  dimension 
}: DimensionDetailDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showToolSheet, setShowToolSheet] = useState(false);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  
  if (!dimension) return null;

  const Icon = dimension.icon;
  const info = dimensionInfo[dimension.dimension];
  
  // Tools that can be accessed inline within the daily check-in
  const inlineAccessibleTools = ['Vibrational Frequency tracker'];
  
  // Map activities to their navigation routes
  const activityRoutes: Record<string, string> = {
    'Sleep tracking': '/sleep-and-dreams',
    'Exercise logging': '/daily-checkin', // Will navigate to exercise section
    'Breathwork': '/meditations',
    'Morning routine': '/daily-checkin',
    'Emotion journaling': '/therapy',
    'Sanctuary skills': '/sanctuary',
    'Therapy chat': '/therapy',
    'Grounding exercises': '/sanctuary',
    'Meditation practice': '/meditations',
    'Brain dump journaling': '/therapy',
    'Digital detox': '/daily-checkin',
    'Nature time': '/daily-checkin',
    'Gratitude journal': '/therapy',
    'Oracle cards': '/telempathy-tools',
    'Telempathy tools': '/telempathy-tools',
    'Community connection': '/connect-and-grow',
    'Shared journeys': '/connect-and-grow',
    'Support groups': '/connect-and-grow',
    'Social activities': '/connect-and-grow',
    'Future Self Navigator': '/future-self',
    'Life Expansion Calendar': '/daily-checkin',
    'Vision board': '/manifestation',
    'Manifestation work': '/manifestation',
    'Sleep insights': '/sleep-and-dreams',
    'Body scan meditation': '/meditations',
    'Nutrition planning': '/daily-checkin'
  };
  
  const handleActivityClick = (activity: string) => {
    if (inlineAccessibleTools.includes(activity)) {
      setSelectedTool(activity);
      setShowToolSheet(true);
    } else {
      const route = activityRoutes[activity];
      if (route) {
        onOpenChange(false); // Close the dialog
        navigate(route);
        toast({
          title: "Navigating to " + activity,
          description: "Use the back button or navigation to return to your check-in.",
        });
      } else {
        toast({
          title: activity,
          description: "This tool will be available soon.",
        });
      }
    }
  };
  
  const renderToolContent = () => {
    switch (selectedTool) {
      case 'Vibrational Frequency tracker':
        return <EnergyFrequencyTracker />;
      default:
        return null;
    }
  };
  
  const getScoreStatus = (score: number) => {
    if (score >= 70) return { label: 'Thriving', color: 'text-primary', bg: 'bg-primary/20', textBg: 'text-primary-foreground' };
    if (score >= 40) return { label: 'Growing', color: 'text-foreground', bg: 'bg-secondary', textBg: 'text-secondary-foreground' };
    return { label: 'Needs Support', color: 'text-accent-foreground', bg: 'bg-accent', textBg: 'text-accent-foreground' };
  };

  const status = getScoreStatus(dimension.score);
  const insights = info.insights(dimension.score);
  const recommendations = info.recommendations(dimension.score);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-4 mb-4">
              <div className={`p-4 rounded-2xl ${dimension.color.replace('text-', 'bg-')}/20`}>
                <Icon className={`w-8 h-8 ${dimension.color}`} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-2xl">{dimension.dimension} Wellness</DialogTitle>
                <DialogDescription className="text-base mt-1">
                  {info.description}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Current Score */}
          <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Current Score
                </h3>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className={`text-5xl font-bold ${dimension.color}`}>
                    {dimension.score}
                  </span>
                  <span className="text-muted-foreground">/100</span>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full ${status.bg}`}>
                <span className={`text-sm font-semibold ${status.textBg}`}>
                  {status.label}
                </span>
              </div>
            </div>
            <Progress value={dimension.score} className="h-3" />
          </Card>

          {/* Insights */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Key Insights
            </h3>
            <div className="space-y-3">
              {insights.map((insight, index) => (
                <Card key={index} className="p-4 bg-muted/30">
                  <div className="flex gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{insight}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Personalised Recommendations
            </h3>
            <div className="space-y-3">
              {recommendations.map((rec, index) => (
                <Card key={index} className="p-4 bg-gradient-to-r from-primary/5 to-transparent border-l-4 border-l-primary/50">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium">{rec}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Tools & Practices */}
          <div>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Related Tools & Practices
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {info.relatedActivities.map((activity, index) => {
                const isInline = inlineAccessibleTools.includes(activity);
                return (
                  <Button
                    key={index}
                    variant="outline"
                    className="justify-between h-auto py-3 text-left hover:bg-primary/10 hover:border-primary/50 transition-all group"
                    onClick={() => handleActivityClick(activity)}
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm font-medium">{activity}</span>
                    </div>
                    {isInline ? (
                      <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                        Open here
                      </span>
                    ) : (
                      <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
                    )}
                  </Button>
                );
              })}
            </div>
            <Card className="mt-4 p-3 bg-primary/5 border-primary/20">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  <strong className="text-foreground">Open here</strong> tools stay in your check-in flow. Others automatically navigate you to their section - simply use your browser back button or main navigation to return.
                </p>
              </div>
            </Card>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      {/* Tool Sheet for Inline Access */}
      <Sheet open={showToolSheet} onOpenChange={setShowToolSheet}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowToolSheet(false)}
                className="mr-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to {dimension.dimension}
              </Button>
            </div>
            <SheetTitle>{selectedTool}</SheetTitle>
            <SheetDescription>
              Use this tool to enhance your {dimension.dimension.toLowerCase()} wellness
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {renderToolContent()}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
